import React, { useState } from "react";
import { Col, Container, Row } from "react-bootstrap";
import heroImage from "../../assets/images/esg-hero.jpg";
import "../../assets/css/pages/esg-sdg-specialist.css";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function LeafIcon() {
  return (
    <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true">
      <path
        fill="currentColor"
        d="M20 3s-9-1-13.5 3.5S3 20 3 20s.7-3.4 3.2-6c1.6-1.6 3.7-2.7 6.1-3.3-2 1.4-4 3.7-5.2 6.7C9 16.6 12.4 15 15.2 12.2 19.7 7.7 20 3 20 3z"
      />
    </svg>
  );
}

function GlobeIcon() {
  return (
    <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="9" />
      <path d="M3 12h18" />
      <path d="M12 3c2.8 3 4.2 6 4.2 9s-1.4 6-4.2 9c-2.8-3-4.2-6-4.2-9S9.2 6 12 3z" />
    </svg>
  );
}

function BriefcaseIcon() {
  return (
    <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="7" width="18" height="13" rx="2" />
      <path d="M9 7V5a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2" />
      <path d="M3 13h18" />
    </svg>
  );
}

function CheckBullet({ tone }) {
  return (
    <span className={`esg-bullet esg-bullet-${tone}`} aria-hidden="true">
      <svg viewBox="0 0 16 16" width="12" height="12">
        <path
          fill="none"
          stroke="#fff"
          strokeWidth="2.4"
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M3.5 8.5l3 3 6-7"
        />
      </svg>
    </span>
  );
}

function InfoCard({ tone, pillLabel, icon, children, className }) {
  return (
    <div className={`esg-card esg-card-${tone} ${className || ""}`}>
      <div className="esg-card-head">
        <span className={`esg-card-icon esg-card-icon-${tone}`} aria-hidden="true">
          {icon}
        </span>
        <span className={`esg-card-pill esg-card-pill-${tone}`}>{pillLabel}</span>
      </div>
      {children}
    </div>
  );
}

function EsgSdgSpecialist() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
    consent: false,
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
    if (errors[name]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[name];
        return next;
      });
    }
  };

  const validate = () => {
    const next = {};
    if (!form.name.trim()) next.name = "Please enter your name.";
    if (!form.email.trim()) next.email = "Please enter your email.";
    else if (!EMAIL_RE.test(form.email.trim())) next.email = "Please enter a valid email address.";
    if (!form.phone.trim()) next.phone = "Please enter your phone number.";
    if (!form.message.trim()) next.message = "Please tell us a little about what you're looking for.";
    if (!form.consent) next.consent = "Please confirm you agree to be contacted.";
    return next;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError("");
    const v = validate();
    if (Object.keys(v).length) {
      setErrors(v);
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/enquiry/esg-sdg`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          name: form.name.trim(),
          email: form.email.trim(),
          phone: form.phone.trim(),
          message: form.message.trim(),
          consent: form.consent,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.ok) {
        setSubmitted(true);
      } else if (res.status === 422 && data && data.errors) {
        setErrors(data.errors);
      } else {
        setSubmitError("Something went wrong. Please try again in a moment.");
      }
    } catch (err) {
      setSubmitError("Something went wrong. Please try again in a moment.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="esg-page">
      <div className="esg-title-strip">
        <Container>
          <h1>ESG-SDG Specialist Certificate Program</h1>
        </Container>
      </div>

      <section className="esg-hero">
        <Container>
          <Row className="esg-hero-row align-items-center">
            <Col lg={6} md={6} xs={12} className="esg-hero-image-col">
              <img
                className="esg-hero-image"
                src={heroImage}
                alt="Young professional holding a laptop, smiling, with a globe, city skyline, wind turbines and solar panels in the background"
              />
            </Col>
            <Col lg={6} md={6} xs={12} className="esg-hero-text-col">
              <h2 className="esg-hero-title">
                Build a <span className="accent-green">Sustainable</span>
                <br />
                Future Career
              </h2>
              <div className="esg-hero-subtitle-wrap">
                <p className="esg-hero-subtitle">
                  Globally Aligned <span className="accent-green">ESG-SDG</span> Specialist Certification Program
                </p>
              </div>
              <a href="#enquire" className="esg-cta-btn">ENQUIRE NOW</a>
            </Col>
          </Row>
        </Container>
      </section>

      <section className="esg-cards">
        <Container>
          <Row>
            <Col lg={6} xs={12}>
              <InfoCard tone="green" pillLabel="Program Highlights" icon={<LeafIcon />}>
                <ul className="esg-list">
                  <li><CheckBullet tone="green" />Learn ESG, Sustainability &amp; SDGs</li>
                  <li><CheckBullet tone="green" />Work on Real-World Impact Projects</li>
                  <li><CheckBullet tone="green" />Build Leadership &amp; Employability Skills</li>
                  <li><CheckBullet tone="green" />Team-Based Simulations &amp; Portfolio Building</li>
                  <li><CheckBullet tone="green" />Explore Global Career Opportunities</li>
                </ul>
              </InfoCard>
            </Col>
            <Col lg={6} xs={12}>
              <InfoCard tone="teal" pillLabel="Gain Exposure To" icon={<GlobeIcon />}>
                <ul className="esg-list">
                  <li><CheckBullet tone="teal" />UN Sustainable Development Goals</li>
                  <li><CheckBullet tone="teal" />Sustainability Reporting</li>
                  <li><CheckBullet tone="teal" />Climate &amp; ESG Practices</li>
                  <li><CheckBullet tone="teal" />AI Ethics &amp; Social Innovation</li>
                </ul>
              </InfoCard>
            </Col>
            <Col xs={12}>
              <InfoCard
                tone="blue"
                pillLabel="Prepare for Careers In"
                icon={<BriefcaseIcon />}
                className="esg-card-wide"
              >
                <ul className="esg-list esg-list-grid">
                  <li><CheckBullet tone="blue" />ESG Consulting</li>
                  <li><CheckBullet tone="blue" />Responsible Finance</li>
                  <li><CheckBullet tone="blue" />Sustainability Reporting</li>
                  <li><CheckBullet tone="blue" />Development Sector Roles</li>
                  <li><CheckBullet tone="blue" />Corporate Governance</li>
                  <li><CheckBullet tone="blue" />Global Sustainability Careers</li>
                </ul>
              </InfoCard>
            </Col>
          </Row>
        </Container>
      </section>

      <section id="enquire" className="esg-enquiry">
        <Container>
          <Row className="justify-content-center">
            <Col lg={8} md={10} sm={12}>
              <h2 className="esg-enquiry-title">Enquire Now</h2>
              <p className="esg-enquiry-sub">
                Tell us a little about yourself and we'll get back to you with program details.
              </p>

              {submitted ? (
                <div className="esg-success" role="status" aria-live="polite">
                  <h3>Thanks — we've received your enquiry.</h3>
                  <p>A program advisor will be in touch shortly with details on the next intake.</p>
                </div>
              ) : (
                <form className="esg-form" onSubmit={handleSubmit} noValidate>
                  <Row className="g-3">
                    <Col md={6} xs={12}>
                      <div className="esg-field">
                        <label htmlFor="esg-name">Full name</label>
                        <input
                          id="esg-name"
                          name="name"
                          type="text"
                          autoComplete="name"
                          value={form.name}
                          onChange={handleChange}
                          aria-invalid={!!errors.name}
                        />
                        {errors.name && <div className="form-error">{errors.name}</div>}
                      </div>
                    </Col>
                    <Col md={6} xs={12}>
                      <div className="esg-field">
                        <label htmlFor="esg-email">Email</label>
                        <input
                          id="esg-email"
                          name="email"
                          type="email"
                          autoComplete="email"
                          value={form.email}
                          onChange={handleChange}
                          aria-invalid={!!errors.email}
                        />
                        {errors.email && <div className="form-error">{errors.email}</div>}
                      </div>
                    </Col>
                    <Col md={6} xs={12}>
                      <div className="esg-field">
                        <label htmlFor="esg-phone">Phone</label>
                        <input
                          id="esg-phone"
                          name="phone"
                          type="tel"
                          autoComplete="tel"
                          value={form.phone}
                          onChange={handleChange}
                          aria-invalid={!!errors.phone}
                        />
                        {errors.phone && <div className="form-error">{errors.phone}</div>}
                      </div>
                    </Col>
                  </Row>

                  <Row>
                    <Col xs={12}>
                      <div className="esg-field">
                        <label htmlFor="esg-message">How can we help?</label>
                        <textarea
                          id="esg-message"
                          name="message"
                          rows={4}
                          value={form.message}
                          onChange={handleChange}
                          aria-invalid={!!errors.message}
                        />
                        {errors.message && <div className="form-error">{errors.message}</div>}
                      </div>
                    </Col>
                    <Col xs={12}>
                      <div className="esg-field esg-consent">
                        <label htmlFor="esg-consent" className="esg-consent-label">
                          <input
                            id="esg-consent"
                            name="consent"
                            type="checkbox"
                            checked={form.consent}
                            onChange={handleChange}
                            aria-invalid={!!errors.consent}
                          />
                          <span>I agree to be contacted about this program and related opportunities.</span>
                        </label>
                        {errors.consent && <div className="form-error">{errors.consent}</div>}
                      </div>
                    </Col>
                    <Col xs={12}>
                      {submitError && <div className="form-error esg-submit-error">{submitError}</div>}
                      <button type="submit" className="esg-submit-btn" disabled={submitting}>
                        {submitting ? "Sending…" : "SUBMIT ENQUIRY"}
                      </button>
                    </Col>
                  </Row>
                </form>
              )}
            </Col>
          </Row>
        </Container>
      </section>
    </div>
  );
}

export default EsgSdgSpecialist;
